/// The product feature keys the webui gates nav items and routes on.
/// Mirrors the boolean-kind entries in shore's
/// `src/modules/entitlements/catalog/feature-catalog.ts` (FEATURES). The
/// AI-usage / credit-pool keys (`ai_credits`, `max_users`,
/// `writing_assistant`, `pulse_insights`, ...) aren't listed here, they
/// drive the credit meter and writing-assist gate, not nav/route gating.
export const PRODUCT_FEATURE_KEYS = [
  "pulse_surveys",
  "custom_surveys",
  "followup_surveys",
  "kudos",
  "todos",
  "feedback",
  "goals",
  "one_on_ones",
  // Added late, and its absence was the bug: the shore has gated
  // performance_reviews since the free plan shipped, but this list never
  // learned about it, so the type made it impossible to declare
  // `requiresFeature` on the Reviews nav row or wrap its route in
  // `<FeatureGate>`. A plan without the feature therefore showed Reviews as
  // a working page and only refused at cycle creation.
  "performance_reviews",
  // FND-026 whole-branch review: this repeated the exact
  // performance_reviews incident above. `hr_core` gates every hr-approvals
  // route (`RequiresEntitlement(HR_CORE_ENTITLEMENT)`) and is `{ enabled:
  // false }` on every plan in the shore's `plan.registry.ts` until
  // HRMS-FND-030, but the Approvals nav item had no `requiresFeature` and
  // its route had no `<FeatureGate>`, so every user saw a working "Approvals"
  // row that always 403'd into a hard error card on click.
  "hr_core",
  // PAY-002. Its own key rather than riding on `hr_core`: payroll has to be
  // able to stay dark on a workspace that already has the rest of HR lit.
  // Dark on every plan until HRMS-PAY-050, so a `<FeatureGate>` around the
  // payroll routes and a `requiresFeature` on the nav row are what keep it
  // from looking like a working page that always 403s, which is exactly the
  // mistake performance_reviews and hr_core each made once already.
  "payroll",
  // HRMS compensation planning (CMP-001): dark on every plan until
  // HRMS-CMP-084 lights it on elevate. Declared now so the nav row and the
  // route FeatureGate can reference it from day one (the performance_reviews
  // and hr_core incidents both came from skipping this step).
  "compensation",
  // The remaining five HRMS module keys. Mirrors the shore catalog, same
  // commit (the cross-app contract). All dark until each module's gating
  // story flips it, so every one of them needs a `requiresFeature` on its nav
  // row and a `<FeatureGate>` on its route, which is the lesson
  // performance_reviews and hr_core each taught once already.
  "leave",
  "attendance",
  "expenses",
  "recruitment",
  "lifecycle",
  "training",
] as const;

export type FeatureKey = (typeof PRODUCT_FEATURE_KEYS)[number];

/// Display labels for the shore plan registry keys
/// (`pulsewise-shore/src/modules/entitlements/plans/plan.registry.ts` PLANS).
/// `entitlementsResponseSchema.plan` carries the raw key ("pulse", not
/// "Pulse"). Fix (final review, IMPORTANT 2): this used to map only the
/// three original subscription tiers; the registry has shipped seven plans
/// since (`free` plus the three `ltd_*` lifetime-deal tiers), and the raw-key
/// fallback below was mistakenly treated as sufficient for the new ones. The
/// map IS documented as covering every registry key, so a plan added to the
/// registry going stale here is the actual bug, not the fallback existing.
/// Names verified against `plan.registry.ts`'s `PLANS` array on 2026-08-03,
/// not guessed: `ltd_tier1`/`ltd_tier2`/`ltd_tier3`'s `name` field is
/// "Lifetime Starter"/"Lifetime Growth"/"Lifetime Scale" respectively.
/// Falls back to the raw value for anything still unmapped (a future plan
/// key added to the registry before this map catches up), this also covers
/// the mock workspace plan labels ("Free"/"Team"/"Pro") shown for anonymous
/// dev sessions in `sidebar-workspace.constants.ts`, which were never real
/// entitlements plans.
const PLAN_KEY_LABELS: Record<string, string> = {
  free: "Free",
  pulse: "Pulse",
  momentum: "Momentum",
  elevate: "Elevate",
  ltd_tier1: "Lifetime Starter",
  ltd_tier2: "Lifetime Growth",
  ltd_tier3: "Lifetime Scale",
};

export function resolvePlanLabel(plan: string): string {
  return PLAN_KEY_LABELS[plan] ?? plan;
}

/// Display labels for the nav-lock + upgrade-dialog copy (W4). Keyed by the
/// same `FeatureKey` union so adding a catalog key forces a label here too
/// (missing-key would be a TS error, not a silent fallback).
const FEATURE_KEY_LABELS: Record<FeatureKey, string> = {
  pulse_surveys: "Pulse surveys",
  custom_surveys: "Custom surveys",
  followup_surveys: "Follow-up surveys",
  kudos: "Kudos",
  todos: "Todos",
  feedback: "Feedback",
  goals: "Goals",
  one_on_ones: "1:1s",
  performance_reviews: "Performance reviews",
  // Labels the ENTITLEMENT, not a single surface it happens to gate.
  // HRMS-FND-030 put `settings/hr` and the people HR tab behind this key
  // alongside Approvals, so three surfaces now share it and
  // `resolveFeatureLabel` feeds the upgrade-card copy on all three. A
  // per-surface label ("Approvals") would read as wrong copy on the
  // settings page or the people HR tab, so the name here has to be the
  // thing being sold, not the first place it was noticed missing.
  hr_core: "HR core",
  // Labels the entitlement, not one surface: the key gates payroll setup, the
  // run wizard, and the self-service payslips page alike.
  payroll: "Payroll",
  compensation: "Compensation",
  // `FEATURE_KEY_LABELS` is `Record<FeatureKey, string>`, so a key added
  // above without a label here is a compile error rather than a blank
  // upgrade card. Each label names the thing being sold, not the first
  // surface that happens to be gated by it.
  leave: "Leave",
  attendance: "Attendance",
  expenses: "Expenses",
  recruitment: "Recruitment",
  lifecycle: "Employee lifecycle",
  training: "Training",
};

export function resolveFeatureLabel(key: FeatureKey): string {
  return FEATURE_KEY_LABELS[key];
}
